import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { NAV } from "@/lib/content";
import { endpoints, api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useQuery } from "@tanstack/react-query";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const { t, isAr } = useLang();
  const { data: settings = {} } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const statement = (isAr ? settings.footer_statement_ar : settings.footer_statement) || t("Building ventures, partnerships and relationships across Saudi Arabia and global markets.");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await endpoints.newsletter(email);
      toast.success("Thank you. You are subscribed.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <footer data-testid="site-footer" className="relative border-t border-primary-gold/10 bg-obsidian">
      <div className="container-x py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Logo width={180} />
            <p className="mt-8 max-w-sm text-[15px] leading-relaxed text-muted-text">
              {statement}
            </p>
            <form onSubmit={subscribe} className="mt-8 flex items-center gap-3 max-w-sm" data-testid="newsletter-form">
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={t("Email for updates")} className="field flex-1" data-testid="newsletter-email"
              />
              <button type="submit" disabled={busy} className="text-primary-gold hover:text-luxury-gold transition-colors p-2" data-testid="newsletter-submit" aria-label="Subscribe">
                <ArrowRight strokeWidth={1.5} />
              </button>
            </form>
          </div>

          <div className="lg:col-span-4 lg:col-start-7">
            <p className="eyebrow mb-6">{t("Navigate")}</p>
            <ul className="grid grid-cols-2 gap-y-3 gap-x-6">
              {[...NAV, { label: "Contact", to: "/contact" }].map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="text-[15px] text-soft-stone hover:text-primary-gold transition-colors">{t(n.label)}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="eyebrow mb-6">{t("Connect")}</p>
            <a href={settings.linkedin_url || "https://linkedin.com"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-soft-stone hover:text-primary-gold transition-colors" data-testid="footer-linkedin">
              <Linkedin size={18} strokeWidth={1.5} /> {t("LinkedIn")}
            </a>
            <p className="mt-6 text-[15px] text-muted-text">{settings.location || t("Saudi Arabia")}</p>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-muted-text">{t("© {year} Intersect Holding. All rights reserved.").replace("{year}", new Date().getFullYear())}</p>
          <div className="flex items-center gap-6 text-xs text-muted-text">
            <Link to="/privacy" className="hover:text-primary-gold transition-colors">{t("Privacy")}</Link>
            <Link to="/terms" className="hover:text-primary-gold transition-colors">{t("Terms")}</Link>
            <Link to="/cookies" className="hover:text-primary-gold transition-colors">{t("Cookie Policy")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
